import { PointDefType, PointValue } from "../models/inspections/Inspections";

/**
 * US 876898 / AB#900926 - Full scale (full range) tolerance calibration points.
 *
 * A point of value data type "f" is generated by EAM with three read-only
 * columns and five metadata rows:
 *
 *   Applied         column order 1, implies_failure_flag = false
 *   Indicated       column order 2, implies_failure_flag = true
 *   Indicated After column order 3, implies_failure_flag = true
 *
 *   limit      / low   - range minimum
 *   limit      / high  - range maximum
 *   tolerance% / low   - band, stored as a positive magnitude
 *   tolerance% / high  - band, stored as a positive magnitude
 *   desired    / column - value 1, a COLUMN ORDER pointer, not a target value
 *
 * The error is a percentage of the range, never of Applied, and is never
 * stored - no column exists to hold it. Mobile derives it for display only;
 * EAM owns the pass/fail verdict when results are saved.
 */

export const FULL_RANGE_VALUE_DATA_TYPE = "f";

export const FULL_RANGE_APPLIED_COLUMN = "Applied";
export const FULL_RANGE_INDICATED_COLUMN = "Indicated";
export const FULL_RANGE_INDICATED_AFTER_COLUMN = "Indicated After";

/**
 * Column codes are matched on a normalised form rather than compared
 * literally. The story text spells the third column "Indicated - After" and
 * the EAM handover spells it "Indicated After"; the rule that generates them
 * is not merged yet, so a reading taken in the field must not silently lose
 * its error percentage over a hyphen. Comparison ignores case, spaces,
 * hyphens and underscores.
 */
const normalizeColumnCode = (columnCode?: string): string =>
  `${columnCode ?? ""}`.toLowerCase().replace(/[\s\-_]+/g, "");

/** Columns whose reading is compared against the tolerance band. */
export const FULL_RANGE_MEASURED_COLUMNS = [
  FULL_RANGE_INDICATED_COLUMN,
  FULL_RANGE_INDICATED_AFTER_COLUMN,
];

const NORMALIZED_MEASURED_COLUMNS = FULL_RANGE_MEASURED_COLUMNS.map(
  normalizeColumnCode
);

export const isFullRangePoint = (pointDef?: PointDefType): boolean =>
  pointDef?.ValueDataType === FULL_RANGE_VALUE_DATA_TYPE;

export const isFullRangeMeasuredColumn = (columnCode?: string): boolean =>
  !!columnCode &&
  NORMALIZED_MEASURED_COLUMNS.includes(normalizeColumnCode(columnCode));

/**
 * The after-repair reading. Called out on its own because it is the one
 * column of a full range point that is legitimately left empty: a calibration
 * that passes as received never gets a repair pass, so a required point must
 * not demand a value here.
 */
export const isFullRangeAfterRepairColumn = (columnCode?: string): boolean =>
  !!columnCode &&
  normalizeColumnCode(columnCode) ===
    normalizeColumnCode(FULL_RANGE_INDICATED_AFTER_COLUMN);

const toNumber = (value: any): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Reads the range and the tolerance band off a full range point's metadata.
 *
 * The generic meta handling cannot be reused here: it derives a tolerance%
 * band from the "desired" meta value, and for a full range point that value is
 * the column order pointer 1. Reading it as a target produces a 0.99 - 1.01
 * band that fails every reading. The same trap was hit and fixed server side.
 */
export const applyFullRangeMeta = (pointDef: PointDefType): PointDefType => {
  let rangeMinimum: number | undefined;
  let rangeMaximum: number | undefined;
  let tolerancePercentLow: number | undefined;
  let tolerancePercentHigh: number | undefined;

  pointDef.Meta?.forEach((meta) => {
    const subtype = `${meta?.MetaSubtype ?? ""}`.trim().toLowerCase();
    const value = toNumber(meta?.MetaValue);
    if (value === undefined) return;

    if (meta.MetaType === "limit") {
      if (subtype === "low") rangeMinimum = value;
      if (subtype === "high") rangeMaximum = value;
    } else if (meta.MetaType === "tolerance%") {
      // Stored as positive magnitudes; the sign is applied at comparison time.
      if (subtype === "low") tolerancePercentLow = Math.abs(value);
      if (subtype === "high") tolerancePercentHigh = Math.abs(value);
    }
    // "desired" / "column" is deliberately ignored - see the note above.
  });

  pointDef.FullRange = true;
  pointDef.RangeMinimum = rangeMinimum;
  pointDef.RangeMaximum = rangeMaximum;
  pointDef.TolerancePercentLow = tolerancePercentLow;
  pointDef.TolerancePercentHigh = tolerancePercentHigh;

  // HighValue / LowValue stay the range so anything reading them for display
  // sees the scale. They are not used to bound the input: a reading may
  // legitimately fall outside the range, which is exactly what the error
  // percentage is there to quantify.
  pointDef.LowValue = rangeMinimum ?? Number.MIN_VALUE;
  pointDef.HighValue = rangeMaximum ?? Number.MAX_VALUE;

  return pointDef;
};

/**
 * Error % = ((Indicated - Applied) / |Maximum - Minimum|) x 100
 *
 * A percentage of the full scale, not of the applied value. Returns undefined
 * when the point is not set up well enough to compute one.
 *
 * The span is taken as a magnitude. A descending range - minimum 200, maximum
 * 100 - describes the same scale as the ascending one, but dividing by a
 * negative span flips the sign of the error while leaving its magnitude
 * intact. That is invisible on a symmetric band and in the displayed value,
 * which is absolute either way; on an asymmetric band it inverts the verdict,
 * so a reading that should fail the narrow side silently passes the wide one.
 * Only (Indicated - Applied) may decide the sign. The setup rule that would
 * reject a descending range lives in work not yet merged, so this cannot lean
 * on the server to have filtered it out.
 */
export const computeFullRangeErrorPercent = (
  applied: number | undefined,
  indicated: number | undefined,
  rangeMinimum: number | undefined,
  rangeMaximum: number | undefined
): number | undefined => {
  if (
    applied === undefined ||
    indicated === undefined ||
    rangeMinimum === undefined ||
    rangeMaximum === undefined
  ) {
    return undefined;
  }
  const span = Math.abs(rangeMaximum - rangeMinimum);
  if (!Number.isFinite(span) || span === 0) return undefined;
  return ((indicated - applied) / span) * 100;
};

/**
 * Signed comparison against the band, matching the server rule. tolerance%
 * low is subtracted and high is added, so the low magnitude must not be
 * negated again. For a symmetric band a comparison on the absolute error
 * agrees with this, which is why conflating the two is easy to miss.
 */
export const isFullRangeErrorOutOfTolerance = (
  errorPercent: number | undefined,
  tolerancePercentLow: number | undefined,
  tolerancePercentHigh: number | undefined
): boolean => {
  if (errorPercent === undefined) return false;
  if (tolerancePercentLow === undefined && tolerancePercentHigh === undefined) {
    return false;
  }
  const low = -Math.abs(tolerancePercentLow ?? tolerancePercentHigh ?? 0);
  const high = Math.abs(tolerancePercentHigh ?? tolerancePercentLow ?? 0);
  return errorPercent < low || errorPercent > high;
};

const findMeasure = (
  pointValues: PointValue[],
  pointId: number,
  columnCode: string
): number | undefined => {
  const wanted = normalizeColumnCode(columnCode);
  return toNumber(
    pointValues.find(
      (pv) =>
        pv.PointId === pointId && normalizeColumnCode(pv.ColumnCode) === wanted
    )?.Measure
  );
};

/**
 * Error for one measured column of a full range point, computed against the
 * single stored Applied value. The legacy Access report prints Applied twice
 * for readability, but there is only ever one.
 */
export const getFullRangeErrorPercent = (
  pointDef: PointDefType,
  columnCode: string,
  pointValues: PointValue[]
): number | undefined => {
  if (!isFullRangePoint(pointDef) || !isFullRangeMeasuredColumn(columnCode)) {
    return undefined;
  }
  return computeFullRangeErrorPercent(
    findMeasure(pointValues, pointDef.PointId, FULL_RANGE_APPLIED_COLUMN),
    findMeasure(pointValues, pointDef.PointId, columnCode),
    pointDef.RangeMinimum,
    pointDef.RangeMaximum
  );
};

/**
 * True when the point carries an after-repair reading that parses as a number.
 */
export const hasFullRangeAfterRepairMeasure = (
  pointDef: PointDefType,
  pointValues: PointValue[]
): boolean =>
  findMeasure(
    pointValues,
    pointDef.PointId,
    FULL_RANGE_INDICATED_AFTER_COLUMN
  ) !== undefined;

/**
 * The column whose reading decides this point's verdict.
 *
 * AB#913492. The after-repair reading supersedes the as-received one: an
 * instrument that arrives out of tolerance and is then corrected has passed
 * its calibration. Indicated therefore decides only when no Indicated After
 * was recorded.
 */
export const fullRangeDecidingColumn = (
  pointDef: PointDefType,
  pointValues: PointValue[]
): string =>
  hasFullRangeAfterRepairMeasure(pointDef, pointValues)
    ? FULL_RANGE_INDICATED_AFTER_COLUMN
    : FULL_RANGE_INDICATED_COLUMN;

/**
 * The verdict for one full range point - true when the point fails.
 *
 * Mirrors FullRangeToleranceHelper.cs on the server, which owns the verdict
 * when results are saved. The two suites assert the same nine cases.
 */
export const isFullRangePointFailed = (
  pointDef: PointDefType,
  pointValues: PointValue[]
): boolean => {
  if (!isFullRangePoint(pointDef)) return false;

  return isFullRangeErrorOutOfTolerance(
    getFullRangeErrorPercent(
      pointDef,
      fullRangeDecidingColumn(pointDef, pointValues),
      pointValues
    ),
    pointDef.TolerancePercentLow,
    pointDef.TolerancePercentHigh
  );
};

/**
 * Recomputes both flags for every measured column of a full range point.
 *
 * OutOfToleranceFlag marks a reading outside the band and drives how it is
 * drawn. FailedFlag is the point's verdict and drives completion; it lands
 * only on the deciding column, so an as-received error superseded by a good
 * repair pass is still shown to the technician without demanding a failure
 * declaration of them.
 *
 * Needed as a whole-point pass rather than a per-keystroke one because
 * changing Applied invalidates the error of both Indicated columns, and
 * entering or clearing an Indicated After flips which reading decides.
 * "Indicated After" is legitimately empty until a repair pass happens - and
 * never gets one when the calibration passes as received - so an incomplete
 * pair is skipped rather than failed, matching the server.
 */
export const recalculateFullRangePointValues = (
  pointDef: PointDefType,
  pointValues: PointValue[]
): PointValue[] => {
  if (!isFullRangePoint(pointDef)) return pointValues;

  const failureColumns = new Set(
    (pointDef.Columns ?? [])
      .filter(
        (col) =>
          col.ImpliesFailureFlag && isFullRangeMeasuredColumn(col.ColumnCode)
      )
      .map((col) => normalizeColumnCode(col.ColumnCode))
  );

  const decidingColumn = normalizeColumnCode(
    fullRangeDecidingColumn(pointDef, pointValues)
  );

  return pointValues.map((pv) => {
    if (
      pv.PointId !== pointDef.PointId ||
      !failureColumns.has(normalizeColumnCode(pv.ColumnCode))
    ) {
      return pv;
    }
    const errorPercent = getFullRangeErrorPercent(
      pointDef,
      pv.ColumnCode,
      pointValues
    );
    const outOfTolerance = isFullRangeErrorOutOfTolerance(
      errorPercent,
      pointDef.TolerancePercentLow,
      pointDef.TolerancePercentHigh
    );
    const failed =
      outOfTolerance && normalizeColumnCode(pv.ColumnCode) === decidingColumn;

    return pv.FailedFlag === failed && pv.OutOfToleranceFlag === outOfTolerance
      ? pv
      : { ...pv, FailedFlag: failed, OutOfToleranceFlag: outOfTolerance };
  });
};

const formatNumber = (value: number | undefined): string =>
  value === undefined ? "" : `${Number.parseFloat(value.toFixed(4))}`;

/** "-1% / +1%", or "" when the point carries no band. */
export const formatFullRangeToleranceBand = (
  pointDef: PointDefType
): string => {
  const { TolerancePercentLow: low, TolerancePercentHigh: high } = pointDef;
  if (low === undefined && high === undefined) return "";
  return `-${formatNumber(low ?? high)}% / +${formatNumber(high ?? low)}%`;
};

/** "100 - 200", or "" when the point carries no range. */
export const formatFullRangeScale = (pointDef: PointDefType): string => {
  const { RangeMinimum: minimum, RangeMaximum: maximum } = pointDef;
  if (minimum === undefined || maximum === undefined) return "";
  return `${formatNumber(minimum)} - ${formatNumber(maximum)}`;
};

/**
 * Displayed error, absolute and to two decimals, matching the report the
 * customer uses today. The comparison that decides pass/fail stays signed.
 */
export const formatFullRangeErrorPercent = (
  errorPercent: number | undefined
): string =>
  errorPercent === undefined ? "" : `${Math.abs(errorPercent).toFixed(2)}%`;
